import TomSelect from 'tom-select'
import I18n from './i18n-settings.js.erb'

const SELECT_CLASS_SELECTOR = 'select.tom-select, select.tom-select-no-placeholder, select.tom-select-no-search'
const FALLBACK_SELECT_TEXT = 'Bitte auswählen ...'
const FALLBACK_NO_RESULTS_TEXT = 'Keine Ergebnisse'
const EMPTY_SEARCH_INPUT = '<input type="search" autocomplete="off" size="1" placeholder="" />'

const noResultsMarkup = () => {
  const text = I18n.t('navigation.no_results')
  const noResultsText = (typeof text === 'string' && text !== 'navigation.no_results' && !text.startsWith('[missing ')) ? text : FALLBACK_NO_RESULTS_TEXT
  return `<div class="no-results">${noResultsText}</div>`
}

const selectPlaceholder = () => {
  const text = I18n.t('navigation.select')
  return (typeof text === 'string' && text !== 'navigation.select' && !text.startsWith('[missing ')) ? text : FALLBACK_SELECT_TEXT
}

const buildOptions = (el) => {
  const options = {
    allowEmptyOption: true,
    render: { no_results: noResultsMarkup }
  }

  if (!el.classList.contains('tom-select-no-placeholder')) {
    options.placeholder = selectPlaceholder()
  }

  if (el.classList.contains('tom-select-no-search')) {
    options.controlInput = null
  } else {
    options.plugins = ['dropdown_input']
    options.controlInput = EMPTY_SEARCH_INPUT
  }

  // Tom Select otherwise stops after 50 options. The vehicle maker list is
  // longer than that and is scrolled through, so it has to render every
  // option. Search still narrows them.
  if (el.classList.contains('tom-select-all-options')) {
    options.maxOptions = null
  }

  // Both maker groups run A–Z, also while searching. Tom Select otherwise
  // orders by source position, and a top make, rendered in both groups,
  // keeps its "Top-Marken" position in "Alle Marken" too. Naming $score
  // stops search from ranking by match quality first. Sorting by label
  // would also move the groups around, so their order is locked.
  if (el.classList.contains('tom-select-makers')) {
    options.sortField = [{ field: 'text' }, { field: '$score' }]
    options.lockOptgroupOrder = true
  }

  return options
}

const filterOptgroupOptions = (rawHtml, selectedValue, selectedLabel = '', selectedIndex = null) => {
  if (!rawHtml) return null

  const targetValue = selectedValue == null ? '' : String(selectedValue).trim()
  const targetLabel = selectedLabel == null ? '' : String(selectedLabel).trim()
  const selectEl = document.createElement('select')
  selectEl.innerHTML = rawHtml
  const groups = Array.from(selectEl.querySelectorAll('optgroup'))

  if (targetValue !== '') {
    const valueMatch = groups.find((group) => (group.getAttribute('label') || '').trim() === targetValue)
    if (valueMatch) return valueMatch.innerHTML
  }

  if (targetLabel !== '') {
    const labelMatch = groups.find((group) => (group.getAttribute('label') || '').trim() === targetLabel)
    if (labelMatch) return labelMatch.innerHTML
  }

  const index = Number.isInteger(selectedIndex) ? selectedIndex : Number.parseInt(selectedIndex, 10)
  if (Number.isInteger(index) && index > 0 && groups[index - 1]) {
    return groups[index - 1].innerHTML
  }

  return null
}

const createTomSelect = (el) => {
  if (!el || el.tomselect) return el?.tomselect

  if (el.form) {
    // Browser native validation doesn't play well with Tom Select hiding the original
    // <select> (some required controls become "not focusable"). Keep server-side
    // validation + inline errors as the source of truth on Tom Select forms.
    el.form.setAttribute('novalidate', 'novalidate')
  }

  const options = buildOptions(el)
  const tomSelect = new TomSelect(el, options)

  if (!el.classList.contains('tom-select-no-placeholder')) {
    if (tomSelect.getValue() === '') tomSelect.clear(true)
    if (Object.prototype.hasOwnProperty.call(tomSelect.options, '')) {
      tomSelect.removeOption('')
      tomSelect.refreshOptions(false)
    }
  }

  const placeholderInput = tomSelect.control.querySelector('.items-placeholder')
  if (placeholderInput) {
    placeholderInput.setAttribute('readonly', 'readonly')
    placeholderInput.tabIndex = -1
  }

  // Keep dropdown search input empty (icon-only) to match the legacy UI.
  tomSelect.on('dropdown_open', () => {
    tomSelect.setTextboxValue('')
    if (tomSelect.control_input) tomSelect.control_input.placeholder = ''
  })

  if (tomSelect.control_input) tomSelect.control_input.placeholder = ''

  return tomSelect
}

const initTomSelect = () => {
  document.querySelectorAll(SELECT_CLASS_SELECTOR).forEach(el => {
    if (el.tomselect) return
    createTomSelect(el)
  })
}

function refreshTomSelect(target) {
  if (!target) return

  const elements = target?.jquery ? target.toArray() : (Array.isArray(target) ? target : [target])

  elements.forEach((domEl) => {
    if (!domEl || typeof domEl.matches !== 'function') return

    const selectedValues = domEl.multiple
      ? Array.from(domEl.selectedOptions || []).map((option) => option.value)
      : domEl.value
    const currentHtml = domEl.innerHTML
    const currentDisabled = domEl.disabled

    if (domEl.tomselect) {
      domEl.tomselect.destroy()
      // Tom Select restores the DOM to the state it captured at init time.
      // Preserve the caller's freshly-updated <option>/<optgroup> markup instead.
      domEl.innerHTML = currentHtml
      domEl.disabled = currentDisabled
    }

    if (!domEl.matches(SELECT_CLASS_SELECTOR)) {
      return
    }

    const selectedValueSet = new Set(Array.isArray(selectedValues) ? selectedValues : [selectedValues])
    Array.from(domEl.options || []).forEach((option) => {
      option.selected = selectedValueSet.has(option.value)
    })

    if (!domEl.multiple && typeof selectedValues === 'string') {
      domEl.value = selectedValues
    }

    createTomSelect(domEl)
  })
}

function destroyAllTomSelect() {
  document.querySelectorAll('.tomselected').forEach(el => {
    if (el.tomselect) {
      el.tomselect.destroy()
    }
  })
}

global.refreshTomSelect = refreshTomSelect
global.filterOptgroupOptions = filterOptgroupOptions

document.addEventListener('turbo:before-cache', destroyAllTomSelect)
document.addEventListener('turbo:before-render', destroyAllTomSelect)

document.addEventListener('turbo:load', initTomSelect)
document.addEventListener('turbo:render', initTomSelect)
